import { Construction, Hourglass } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ComingSoonCard } from "@/lib/flags/ComingSoonCard";
import type { FeatureFlag } from "@/lib/flags/flags";

interface SectionPageProps {
  /** Full i18n key of the page title (e.g. `nav.items.orders_all`). */
  titleKey: string;
  /** Phase-gated screen (R18) — says "coming in a later phase" instead of "on its way". */
  comingSoon?: boolean;
  /** Integrations design/04 places on this screen (R18): shown as "coming soon" cards. */
  features?: readonly FeatureFlag[];
}

/**
 * Every menu entry opens a real page from day one (T11-01): the standard header + a clear next
 * step. The screens themselves replace this component task by task (T11-10 … T11-47).
 */
export function SectionPage({ titleKey, comingSoon, features = [] }: SectionPageProps) {
  const { t } = useTranslation();
  const title = t(titleKey);
  return (
    <div className="flex flex-col gap-6" data-section={titleKey}>
      <PageHeader title={title} />
      <EmptyState
        icon={comingSoon ? Hourglass : Construction}
        title={comingSoon ? t("states.coming_soon_title") : t("states.building_title")}
        body={
          comingSoon
            ? t("states.coming_soon_body", { title })
            : t("states.building_body", { title })
        }
        action={
          <Button asChild variant="outline">
            <Link to="/">{t("states.building_action")}</Link>
          </Button>
        }
      />
      {features.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2" data-slot="coming-soon-list">
          {features.map((feature) => (
            <ComingSoonCard key={feature} feature={feature} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
